/**
 * Dal codice d'errore del contratto alla chiave di traduzione (CF12/CF13).
 *
 * Il contratto risponde con **codici** maiuscoli (`CONFIRMATION_STALE`) e, dove
 * può, con la chiave già risolta (`messageKey`). Dove la chiave non c'è — o non
 * è arrivata, perché il percorso passa dal motore legacy — il client deve
 * sapere a quale voce del catalogo corrisponde il codice.
 *
 * Senza questa mappa il client componeva `custom_fields.error.` + il codice:
 * `custom_fields.error.CONFIRMATION_STALE`, che nel catalogo non esiste e non
 * esisterà mai, perché le chiavi sono minuscole. Il risultato era una chiave
 * grezza a schermo **proprio nei messaggi d'errore**, cioè dove serve capire.
 *
 * ## Perché è una copia, e come non farla divergere
 *
 * La fonte è `app/schemas/custom_fields.py::ERROR_MESSAGE_KEYS` (nel servizio
 * Python). Il client non può importarla: sono due repository. Quindi è una
 * copia dichiarata, e le sue chiavi finiscono nel catalogo perché
 * `translations.spec.ts` le vede citate qui — se il servizio aggiungesse un
 * codice e il client no, la chiave mancherebbe **al catalogo**, non a questo
 * file, e il test del catalogo la elencherebbe.
 *
 * `CUSTOM_FIELDS_MESSAGE_KEYS` in `messages.ts` è l'elenco delle famiglie da
 * chiedere al deploy: questa mappa dice quali voci concrete servono.
 */

/** codice del contratto → chiave di traduzione. */
export const ERROR_MESSAGE_KEYS: Readonly<Record<string, string>> = {
  VALIDATION_FAILED: "custom_fields.error.validation_failed",
  FIELD_READ_ONLY: "custom_fields.error.field_read_only",
  FIELD_ACCESS_DENIED: "custom_fields.error.field_access_denied",
  REVISION_CONFLICT: "custom_fields.error.revision_conflict",
  REQUIRED_FIELDS_MISSING: "custom_fields.error.required_fields_missing",
  DUPLICATE_CONFIRMATION_REQUIRED: "custom_fields.sequence.duplicate.message",
  DUPLICATE_CODE_REJECTED: "custom_fields.sequence.duplicate.automatic_rejected",
  CONFIRMATION_STALE: "custom_fields.error.confirmation_stale",
  OPERATION_ID_CONFLICT: "custom_fields.error.operation_id_conflict",
  OPERATION_EXPIRED: "custom_fields.error.operation_expired",
  FORMULA_INVALID: "custom_fields.formula.invalid",
  FORMULA_DIVISION_BY_ZERO: "custom_fields.formula.division_by_zero",
  FORMULA_NUMERIC_OVERFLOW: "custom_fields.formula.numeric_overflow",
  CUSTOM_FIELDS_UNAVAILABLE: "custom_fields.error.unavailable",
  RESOURCE_NOT_FOUND: "custom_fields.error.not_found",
  INVALID_DECIMAL: "custom_fields.error.invalid_decimal",
  INVALID_DATE: "custom_fields.error.invalid_date",
  INVALID_EMAIL: "custom_fields.error.invalid_email",
  INVALID_URL: "custom_fields.error.invalid_url",
  INVALID_PHONE: "custom_fields.error.invalid_phone",
  INVALID_RANGE: "custom_fields.error.invalid_range",
  INVALID_DEFAULT: "custom_fields.error.invalid_default",
  // Un campo obbligatorio che l'operatore non può scrivere: il blocco è reale e
  // la spiegazione non deve rivelare il campo, dice a chi rivolgersi.
  REQUIRED_FIELDS_BLOCKED: "custom_fields.required.blocked_contact",
};

/** La chiave da mostrare per un rifiuto. */
export function messageKeyFor(
  error: { code?: string | null; messageKey?: string | null } | null | undefined,
  fallback: string = "custom_fields.error.generic"
): string {
  // La chiave risolta dal server vince: è il contratto che sa cosa ha rifiutato,
  // e un giorno potrebbe mappare lo stesso codice a una voce più precisa.
  if (error?.messageKey) return error.messageKey;
  if (error?.code && ERROR_MESSAGE_KEYS[error.code]) {
    return ERROR_MESSAGE_KEYS[error.code];
  }
  return fallback;
}
